import { useCallback, useState } from "react";
import { ROLE_KEYS, type RoleKey } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { Button } from "../ui/button.js";
import { Modal } from "../ui/modal.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import type { UserDto } from "@grizzly/shared";
import { useI18n, type MessageKey } from "../../i18n/index.js";

interface CreateForm {
  email: string;
  displayName: string;
  role: RoleKey;
  password: string;
}

const EMPTY_FORM: CreateForm = {
  email: "",
  displayName: "",
  role: "operator",
  password: "",
};

export function CreateUserDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  onCreated(user: UserDto): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const roleOptions = ROLE_KEYS.map((key) => ({
    value: key,
    label: t(`roles.${key}` as MessageKey),
  }));

  const handleCreate = useCallback(async () => {
    setBusy(true);
    setFieldErrors({});
    try {
      const res = await api.createUser(form);
      push(
        t("users.create.createdToast"),
        t("users.create.createdToastDesc", { name: res.user.displayName })
      );
      setForm(EMPTY_FORM);
      onOpenChange(false);
      onCreated(res.user);
    } catch (err) {
      if (err instanceof ApiClientError && err.code === "validation_failed") {
        setFieldErrors(err.fieldIssues());
      } else if (err instanceof ApiClientError) {
        push(
          err.message,
          err.requestId ? t("common.requestId", { id: err.requestId }) : undefined,
          "danger"
        );
      }
    } finally {
      setBusy(false);
    }
  }, [form, onCreated, onOpenChange, push, t]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("users.create.title")}
      description={t("users.create.description")}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={handleCreate} disabled={busy}>
            {busy ? t("common.creating") : t("users.create.submit")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <TextInput
          label={t("users.create.fullName")}
          value={form.displayName}
          error={fieldErrors["displayName"] ?? null}
          onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
        />
        <TextInput
          label={t("common.fields.email")}
          type="email"
          value={form.email}
          error={fieldErrors["email"] ?? null}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        />
        <SelectInput
          label={t("users.role")}
          options={roleOptions}
          value={form.role}
          error={fieldErrors["role"] ?? null}
          onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as RoleKey }))}
        />
        <TextInput
          label={t("users.create.initialPassword")}
          type="password"
          value={form.password}
          error={fieldErrors["password"] ?? null}
          hint={t("users.create.passwordHint")}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
        />
      </div>
    </Modal>
  );
}
